// src/pages/DebtStudio/reports/exportDebtPortfolioPdf.js

import dayjs from "lib/dayjs";
import { createCompassPdf } from "reporting/pdf/createCompassPdf";
import { addKpiCards } from "reporting/pdf/addKpiCards";
import { addReportTable } from "reporting/pdf/addReportTable";
import { addStackedBarChart } from "reporting/pdf/addStackedBarChart";
import { addKeyValueBlock } from "reporting/pdf/pdfLayout";
import { safeFilePart } from "reporting/pdf/pdfFormatters";
import { buildDebtPortfolioReportData } from "./buildDebtPortfolioReportData";

function fmtPeriodEnd(value) {
  const d = dayjs(value);
  if (!d.isValid()) return value || "—";
  return d.format("M/D");
}

function addChartAndTable(ctx, section, chartTitle, startY, options = {}) {
  const { doc, margins } = ctx;
  let y = startY;

  y = addStackedBarChart(
    ctx,
    {
      title: chartTitle,
      rows: section?.chartRows || [],
      series: section?.chartSeries || [],
      height: options.height || 220,
      legend: options.legend !== false,
    },
    y
  );

  y = addReportTable(ctx, section?.table, y);

  if (options.newPageAfter) {
    doc.addPage();
    return margins.top;
  }

  return y;
}

export async function exportDebtPortfolioPdf({
  reportData = null,
  save = true,
  ...builderArgs
} = {}) {
  const data = reportData || buildDebtPortfolioReportData(builderArgs);

  const ctx = await createCompassPdf({
    orgId: builderArgs.orgId,
    reportTitle: data.meta.reportTitle,
    orgName: data.meta.orgName || data.meta.portfolioName,
    asOfLabel: data.meta.asOfLabel,
    createdStamp: new Date(),
    orientation: "portrait",
    branding: "unbranded",
  });

  const { doc, margins } = ctx;
  let y = margins.top + 10;

  y = addKeyValueBlock(
    ctx,
    [
      { label: "Portfolio", value: data.meta.portfolioName || "Portfolio" },
      { label: "Annual Period End", value: fmtPeriodEnd(data.meta.periodEndDate) },
      { label: "Issues", value: data.meta.issueCount },
      ...(data.meta.description
        ? [{ label: "Description", value: data.meta.description }]
        : []),
    ],
    y,
    {
      columns: 2,
      labelWidth: 104,
      rowHeight: 14,
      columnGap: 28,
    }
  );

  y = addKpiCards(ctx, data.kpis || [], y);

  y = addChartAndTable(
    ctx,
    data.byIssueDebtService,
    "Debt Service by Issue",
    y,
    { height: 230, newPageAfter: true }
  );

  y = addChartAndTable(
    ctx,
    data.aggregateDebtService,
    "Aggregate Debt Service",
    y,
    { newPageAfter: true }
  );

  y = addChartAndTable(
    ctx,
    data.principalAllocation,
    "Principal Allocation",
    y,
    { newPageAfter: true }
  );

  addChartAndTable(
    ctx,
    data.callable,
    "Callable / Non-callable Outstanding",
    y
  );

  ctx.finalize();

  const fileName = `DebtStudio_Portfolio_${safeFilePart(
    data.meta.orgName || "Org"
  )}_${safeFilePart(data.meta.portfolioName || "Portfolio")}_${dayjs(
    data.meta.fileDate
  ).format("YYYY-MM-DD")}.pdf`;

  if (save) {
    doc.save(fileName);
  }

  return {
    doc,
    fileName,
    reportData: data,
  };
}

export default exportDebtPortfolioPdf;
